import { useEffect, useState } from "react";
import { Heart, Coins, Loader2, ArrowLeft } from "lucide-react";
import { api } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import Constellation from "@/components/Constellation";
import SkyStrip from "@/components/SkyStrip";
import SignSample from "@/components/SignSample";
import { SignGrid, ModeToggle } from "@/components/SignGrid";
import ShareSignButton from "@/components/ShareSignButton";
import { ExploreSection } from "@/components/Explore";

function Section({ icon: Icon, label, text, testId }) {
  return (
    <div data-testid={testId} className="bg-[#0F1523] border border-[#D4AF37]/20 rounded-2xl p-5">
      <p className="flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] gold-text mb-2">
        {Icon && <Icon size={14} />} {label}
      </p>
      <p className="text-base leading-relaxed text-[#D6D0C5]">{text}</p>
    </div>
  );
}

function SignDetail({ r, onBack, kind }) {
  return (
    <div className="fade-up" data-testid="sign-detail">
      <div className="flex items-center justify-between mb-3">
        <button data-testid="btn-signs-back" onClick={onBack} className="flex items-center gap-1 text-sm gold-text">
          <ArrowLeft size={15} /> All signs
        </button>
        <ShareSignButton sign={r.sign} kind={kind} />
      </div>
      <div className="flex flex-col items-center text-center mb-4">
        <Constellation sign={r.sign} size={110} />
        <h2 className="text-3xl font-light mt-1" data-testid="sign-detail-title">{r.sign}</h2>
        <div className="flex items-center gap-3 mt-2 text-sm text-[#A7B0C0]">
          <span>Mood · <span className="text-[#E8DCC4]">{r.mood}</span></span>
          <span className="w-1 h-1 rounded-full bg-[#D4AF37]/50" />
          <span>Lucky · <span className="text-[#E8DCC4]">{r.lucky_color}</span></span>
        </div>
      </div>
      <div className="space-y-3">
        <Section label={{ daily: "Today", weekly: "This Week", monthly: "This Month" }[kind]} text={r.overall} testId="sign-detail-overall" />
        <Section icon={Heart} label="Love" text={r.love} testId="sign-detail-love" />
        <Section icon={Coins} label="Money" text={r.money} testId="sign-detail-money" />
      </div>
    </div>
  );
}

export default function Signs({ onSubscribe }) {
  const { user } = useAuth();
  const isPremium = user?.is_premium;
  const [mode, setMode] = useState("daily");
  const [sets, setSets] = useState({});
  const [error, setError] = useState(false);
  const [picked, setPicked] = useState(null);

  useEffect(() => {
    if (sets[mode]) return;
    setError(false);
    const url = !isPremium ? "/public/signs" : { daily: "/signs/today", weekly: "/signs/week", monthly: "/signs/month" }[mode];
    api.get(url)
      .then(({ data }) => setSets((p) => ({ ...p, [mode]: data })))
      .catch(() => setError(true));
  }, [isPremium, mode, sets]);

  const changeMode = (m) => (m !== "daily" && !isPremium ? onSubscribe(`${m} outlooks for every sign`) : setMode(m));
  const data = sets[mode];
  const current = data?.signs.find((x) => x.sign === picked);
  const back = () => setPicked(null);

  if (current && !isPremium) {
    return (
      <div className="px-5 pt-5 pb-28" data-testid="signs-page">
        <button data-testid="btn-signs-back" onClick={back} className="flex items-center gap-1 text-sm gold-text mb-3">
          <ArrowLeft size={15} /> All signs
        </button>
        <SignSample r={current} prefix="sample-sign" onUnlock={() => onSubscribe(`the full ${current.sign} reading`)} />
        <button
          data-testid="btn-sample-unlock"
          onClick={() => onSubscribe(`the full ${current.sign} reading`)}
          className="w-full mt-5 bg-[#D4AF37] hover:bg-[#e6c04e] text-[#04060B] font-medium text-lg py-3.5 rounded-full transition-colors"
        >
          Unlock full readings with Premium
        </button>
      </div>
    );
  }

  return (
    <div className="px-5 pt-5 pb-28 fade-up" data-testid="signs-page">
      {current ? (
        <SignDetail r={current} kind={mode} onBack={back} />
      ) : (
        <>
          <p className="text-[11px] uppercase tracking-[0.3em] gold-text">{isPremium ? "Premium" : "Free sample"} · {{ daily: "Daily", weekly: "Weekly", monthly: "Monthly" }[mode]}</p>
          <h1 className="text-3xl font-light mt-1 mb-1">All Star Signs</h1>
          <p className="text-sm text-[#A7B0C0] mb-5">
            {{ daily: "Today's horoscope", weekly: "The week ahead", monthly: "The month ahead" }[mode]} for every sign, perfect for friends, family and partners.
          </p>
          {isPremium && <SkyStrip onSubscribe={onSubscribe} />}
          <ExploreSection sign={user?.trinity?.sun?.sign} />
          <ModeToggle mode={mode} setMode={changeMode} weeklyLocked={!isPremium} />
          {!data && !error && (
            <div className="flex items-center gap-2 text-sm text-[#A7B0C0] mb-4" data-testid="signs-loading">
              <Loader2 size={15} className="animate-spin gold-text" /> Reading the skies for all twelve signs…
            </div>
          )}
          {error && <p className="text-sm text-red-300 mb-4" data-testid="signs-error">Couldn't load the readings. Please try again shortly.</p>}
          <SignGrid onPick={setPicked} />
          {!isPremium && (
            <button
              data-testid="btn-signs-unlock"
              onClick={() => onSubscribe("all 12 star sign readings")}
              className="w-full mt-5 bg-[#D4AF37] hover:bg-[#e6c04e] text-[#04060B] font-medium text-lg py-3.5 rounded-full transition-colors"
            >
              Unlock full readings & weekly outlooks
            </button>
          )}
        </>
      )}
    </div>
  );
}
